import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { TextAreaField, TextField } from '../../components/Field';
import { SkeletonTable } from '../../components/Skeleton';
import { useSession } from '../auth/queries';
import {
  roleDescription,
  roleLabel,
  useCreateRole,
  useDeleteRole,
  usePermissionCatalog,
  useRoles,
  useUpdateRole,
  type PermissionRead,
  type RoleRead,
} from './queries';

/** Was rechts unten bearbeitet wird -- eine bestehende Rolle oder eine neue. */
interface Draft {
  roleId: string | null;
  name: string;
  description: string;
  permissions: string[];
}

/**
 * Die Rollen des Mandanten -- F5-19, K-130.
 *
 * **Der Anlass.** Unter "Zugaenge" liess sich eine Rolle vergeben, aber
 * nirgends sehen, was sie gewaehrt. Diese Seite zeigt es fuer jede Rolle,
 * auch fuer die, die man nicht aendern darf -- deshalb liest sie mit
 * ``users.read`` und schreibt mit ``roles.manage``.
 *
 * **Systemrollen nur ansehen.** Sie gelten fuer alle Mandanten; die
 * Datenbank laesst sie sich nicht aendern (Migration 0056). Statt eines
 * gesperrten Formulars gibt es "Als Vorlage kopieren" -- das ist der Weg zu
 * "Bearbeitung, aber ohne CRM-Schreibrecht".
 *
 * **Nur eigene Rechte sind waehlbar.** Ein Recht, das man selbst nicht
 * traegt, steht ausgegraut da. Der Server weist es ohnehin ab; das
 * Ausgrauen erspart den Fehler, den man vorher sah.
 */
export function RolesPage() {
  const { t } = useTranslation();
  const roles = useRoles();
  const catalog = usePermissionCatalog();
  const session = useSession();
  const create = useCreateRole();
  const update = useUpdateRole();
  const remove = useDeleteRole();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);

  const ownPermissions = useMemo(
    () => new Set(session.data?.permissions ?? []),
    [session.data?.permissions],
  );
  const canManage = ownPermissions.has('roles.manage');
  const alle = roles.data ?? [];
  const selected = alle.find((role) => role.id === selectedId) ?? null;

  const groups = useMemo(() => groupByCategory(catalog.data ?? []), [catalog.data]);

  function open(role: RoleRead) {
    setSelectedId(role.id);
    setDraft(
      role.is_system || !canManage
        ? null
        : {
            roleId: role.id,
            name: role.name,
            description: role.description,
            permissions: [...role.permissions],
          },
    );
    resetMutations();
  }

  function startNew(template?: RoleRead) {
    setSelectedId(null);
    setDraft({
      roleId: null,
      name: template ? t('roles.copyName', { name: roleLabel(template, t) }) : '',
      description: template ? roleDescription(template, t) : '',
      // Aus einer Vorlage nur das, was man selbst vergeben darf -- sonst
      // scheiterte das Speichern an einem Recht, das man nicht abwaehlen kann.
      permissions: (template?.permissions ?? []).filter((key) => ownPermissions.has(key)),
    });
    resetMutations();
  }

  function resetMutations() {
    create.reset();
    update.reset();
    remove.reset();
  }

  function toggle(permission: string) {
    setDraft((current) =>
      current
        ? {
            ...current,
            permissions: current.permissions.includes(permission)
              ? current.permissions.filter((item) => item !== permission)
              : [...current.permissions, permission],
          }
        : current,
    );
  }

  function save() {
    if (!draft) return;
    const payload = {
      name: draft.name.trim(),
      description: draft.description.trim(),
      permissions: draft.permissions,
    };
    if (draft.roleId) {
      update.mutate({ roleId: draft.roleId, payload });
    } else {
      create.mutate(payload, {
        onSuccess: (role) => {
          open(role);
        },
      });
    }
  }

  const shownPermissions = draft ? draft.permissions : (selected?.permissions ?? []);
  const needsMfa = (catalog.data ?? []).some(
    (item) => item.mfa_required && shownPermissions.includes(item.key),
  );
  const isOpen = draft !== null || selected !== null;

  return (
    <>
      <PageHeader
        title={t('roles.title')}
        subtitle={t('roles.subtitle')}
        action={
          canManage ? (
            <Button
              variant="primary"
              onClick={() => {
                startNew();
              }}
            >
              {t('roles.new')}
            </Button>
          ) : null
        }
      />

      {roles.isPending ? <SkeletonTable columns={4} /> : null}
      {roles.isError ? <ErrorNotice error={roles.error} /> : null}
      {catalog.isError ? <ErrorNotice error={catalog.error} /> : null}

      <section className="card card--flush">
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('roles.name')}</th>
                <th scope="col">{t('roles.kind')}</th>
                <th scope="col">{t('roles.permissionCount')}</th>
                <th scope="col">{t('roles.userCount')}</th>
              </tr>
            </thead>
            <tbody>
              {alle.map((role) => (
                <tr
                  key={role.id}
                  data-clickable
                  aria-selected={role.id === (draft?.roleId ?? selectedId)}
                  onClick={() => {
                    open(role);
                  }}
                >
                  <td>
                    <button
                      type="button"
                      className="roles__open"
                      onClick={(event) => {
                        event.stopPropagation();
                        open(role);
                      }}
                    >
                      {roleLabel(role, t)}
                    </button>
                    {role.description ? (
                      <div className="table__muted roles__description">
                        {roleDescription(role, t)}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {role.is_system ? (
                      <Badge tone="neutral">{t('roles.system')}</Badge>
                    ) : (
                      <Badge tone="info">{t('roles.custom')}</Badge>
                    )}{' '}
                    {role.assignable ? null : <Badge tone="neutral">{t('roles.portal')}</Badge>}
                  </td>
                  <td className="table__muted">{role.permissions.length}</td>
                  <td className="table__muted">{role.user_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {isOpen ? (
        <section className="card stack" aria-label={t('roles.detail')}>
          {create.isError ? <ErrorNotice error={create.error} /> : null}
          {update.isError ? <ErrorNotice error={update.error} /> : null}
          {remove.isError ? <ErrorNotice error={remove.error} /> : null}
          {update.isSuccess ? (
            <p className="field__hint" role="status">
              {t('roles.saved')}
            </p>
          ) : null}

          {draft ? (
            <>
              <h2>{draft.roleId ? t('roles.edit') : t('roles.new')}</h2>
              <div className="row">
                <TextField
                  label={t('roles.name')}
                  value={draft.name}
                  onChange={(event) => {
                    setDraft({ ...draft, name: event.target.value });
                  }}
                />
              </div>
              <TextAreaField
                label={t('roles.description')}
                rows={2}
                value={draft.description}
                onChange={(event) => {
                  setDraft({ ...draft, description: event.target.value });
                }}
              />
            </>
          ) : selected ? (
            <>
              <div className="row">
                <h2>{roleLabel(selected, t)}</h2>
                {selected.is_system ? <Badge tone="neutral">{t('roles.system')}</Badge> : null}
              </div>
              {selected.description ? <p>{roleDescription(selected, t)}</p> : null}
              {selected.is_system ? <p className="table__muted">{t('roles.systemHint')}</p> : null}
            </>
          ) : null}

          <div className="role-permissions">
            {groups.map(([category, items]) => (
              <fieldset key={category} className="role-permissions__group">
                <legend className="field__label">
                  {t(`permissionCategory.${category}`, { defaultValue: category })}
                </legend>
                {items.map((item) => {
                  const checked = shownPermissions.includes(item.key);
                  const grantable = ownPermissions.has(item.key);
                  return (
                    <label
                      key={item.key}
                      className="role-permissions__item"
                      title={!draft || grantable ? undefined : t('roles.notGrantable')}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!draft || !grantable}
                        onChange={() => {
                          toggle(item.key);
                        }}
                      />
                      <span>
                        {t(`permission.${item.key.replace('.', '_')}`, {
                          defaultValue: item.description,
                        })}{' '}
                        {item.mfa_required ? (
                          <Badge tone="warning">{t('roles.mfaBadge')}</Badge>
                        ) : null}
                        <code className="role-permissions__key">{item.key}</code>
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            ))}
          </div>

          {needsMfa ? (
            <div className="notice notice--info">
              <span>{t('roles.mfaHint')}</span>
            </div>
          ) : null}

          <div className="row">
            {draft ? (
              <>
                <Button
                  variant="primary"
                  disabled={draft.name.trim().length < 2}
                  busy={create.isPending || update.isPending}
                  onClick={save}
                >
                  {draft.roleId ? t('roles.save') : t('roles.create')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setDraft(null);
                    setSelectedId(null);
                    resetMutations();
                  }}
                >
                  {t('roles.cancel')}
                </Button>
              </>
            ) : null}

            {canManage && selected ? (
              <Button
                variant="secondary"
                onClick={() => {
                  startNew(selected);
                }}
              >
                {t('roles.copy')}
              </Button>
            ) : null}

            {canManage && selected && !selected.is_system ? (
              <Button
                variant="danger"
                disabled={selected.user_count > 0}
                busy={remove.isPending}
                onClick={() => {
                  if (!window.confirm(t('roles.deleteConfirm', { name: selected.name }))) return;
                  remove.mutate(selected.id, {
                    onSuccess: () => {
                      setSelectedId(null);
                      setDraft(null);
                    },
                  });
                }}
              >
                {t('roles.delete')}
              </Button>
            ) : null}
          </div>
          {canManage && selected && !selected.is_system && selected.user_count > 0 ? (
            <p className="field__hint">
              {t('roles.deleteBlocked', { count: selected.user_count })}
            </p>
          ) : null}
        </section>
      ) : null}
    </>
  );
}

/** Der Katalog nach Gruppen, in der Reihenfolge, in der der Server ihn fuehrt. */
function groupByCategory(items: PermissionRead[]): [string, PermissionRead[]][] {
  const groups = new Map<string, PermissionRead[]>();
  for (const item of items) {
    const group = groups.get(item.category) ?? [];
    group.push(item);
    groups.set(item.category, group);
  }
  return [...groups.entries()];
}
